<template lang="pug">
  .wrap
    #unread_sep(v-if="isFirstUnread", v-once) {{ $t('common.unreadMessages', { unreadNumber }) }}
    .chat(:style="{ 'text-align': msgAlign }", :class="{ loading, channel: chatTypeId === 5 }")
      .chat_msg(
        v-if="message.message_type_id === messageTypes.user",
        :class="[isOwn ? 'outcome' : 'income', {'img': message.img, 'group': chatTypeId === 1, 'company': message.company}]")
        .chat_msg__avatar(v-if="!isOwn && chatTypeId !== 5")
          div(v-if="user.avatar_url", :style="{ 'background-image': 'url(' + avatarUrlWithResize(user.avatar_url, '35x35') + ')' }")
          AvatarPlaceholder.sm(v-else, :fullName="user.first_name + ' ' + user.last_name", :classname="getAvatarClass(user.first_name, user.last_name)")
        ModerationMenu(
          top="13px",
          right="-30px",
          type="chat",
          :instanceId="142")
          .chat_msg__body
            .file(v-if="message.file")
              .file_icon
                SvgFileCover.file_cover(dark)
                span.file_format {{ message.fileFormat }}
              .file_info
                .file_name {{ message.fileName }}
                .file_description
                  span {{ message.fileFormat }}
                  span {{ $t('common.file') }}
                  span {{ message.fileSize }}
            .chat_msg__name(v-if="chatTypeId === 1 && !isOwn")
              v-tooltip(top, open-delay="1000", max-width="500")
                template(v-slot:activator="{ on }")
                  span(v-on="on", :class="message.role") {{ user.first_name }} {{ user.last_name }}
                span {{ user.first_name }} {{ user.last_name }}
              .chat_msg__company(v-if="message.companyName") {{ $t('common.preposition.from').toLowerCase() }}
                span.company__avatar(:style="{ 'background-image': 'url(' + message.companyLogo + ')' }")
                span {{ message.companyName }}
            .message {{ message.message }}
            img.file_img(v-if="message.img", :src="message.img")
            intersect(
              @enter="onIntersectEnter",
              :root="intersectOptions.root",
              :threshold="intersectOptions.threshold",
              :rootMargin="intersectOptions.rootMargin")
              .chat_msg__time(:data-id="message.id") {{ message.created_at | messageTime($i18n.locale) }}
        router-link.chat_msg__action(to="", v-if="message.action") {{ $t('actions.connect') }}
      intersect(
        v-else,
        @enter="onIntersectEnter",
        :root="intersectOptions.root",
        :threshold="intersectOptions.threshold",
        :rootMargin="intersectOptions.rootMargin")
        .chat_tech_msg(:data-id="message.id", v-html="techMsgText")
</template>

<script>
import AvatarPlaceholder from '@/components/AvatarPlaceholder';
import ModerationMenu from '@/components/ModerationMenu';
import Intersect from '@/components/Intersect';
import { getAvatarClass, avatarUrlWithResize } from '@/utils';
import CONSTANTS from '@/constants';

import SvgFileCover from '@/components/svg/SvgFileCover';

export default {
  components: {
    AvatarPlaceholder,
    ModerationMenu,
    SvgFileCover,
    Intersect
  },
  props: {
    chatTypeId: { type: Number, default: 1 },
    message: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    unreadNumber: { type: [Number, String], default: 0 },
    root: { default: null }
  },
  data() {
    return {
      getAvatarClass,
      avatarUrlWithResize,
      messageTypes: CONSTANTS.messageTypes,
      intersectOptions: {
        root: null,
        threshold: 1,
        rootMargin: '15px 16px 16px 8px'
      }
    };
  },
  computed: {
    authUser() {
      return this.$store.state.user;
    },
    msgAlign() {
      if (this.message.message_type_id !== this.messageTypes.user) {
        return 'center';
      }

      return this.isOwn ? 'right' : 'left';
    },
    isOwn() {
      return this.message.user_id === this.authUser.id;
    },
    user() {
      return this.$store.state.chats.activeChatUsers[this.message.user_id] || this.message.user || {};
    },
    isFirstUnread() {
      return this.$store.state.chats.activeChatFirstUnread === this.message.id;
    },
    techMsgText() {
      let txt = '';
      const userName = `${this.user.first_name} ${this.user.last_name}`;
      const time = this.$options.filters.messageTime(this.message.created_at, this.$i18n.locale);

      switch (this.message.message_type_id) {
        case this.messageTypes.chatCreated:
          txt = this.$t('messages.technical.chatCreated', { time });
          break;
        case this.messageTypes.chatJoined:
          txt = this.$t('messages.technical.userJoinedChat', { userName, time });
          break;
        case this.messageTypes.chatLeft:
          txt = this.$t('messages.technical.userLeftChat', { userName, time });
          break;
        default: break;
      }

      return txt;
    }
  },
  methods: {
    onIntersectEnter(entry) {
      if (this.loading) return;

      if (entry.target.dataset.id && !this.message.is_read) {
        this.$emit('messageFullyInsected', +entry.target.dataset.id);
      }
    }
  },
  mounted() {
    this.intersectOptions.root = this.root;
  }
};
</script>

<style lang="scss" scoped>
  @import '../assets/scss/variables';

  .wrap {
    display: block;
    width: 100%;
  }

  #unread_sep {
    text-align: center !important;
    background: $light-theme-line;
    margin: 15px 0;
    height: 30px;
    font-size: 12px;
    line-height: 2.5;
    color: $blue;
    font-weight: 600;
    position: relative;
    margin-left: 0;
  }

  .chat {
    opacity: 1;
    transition: opacity .3s $easing;
    margin-left: 52px;

    &.loading { opacity: 0; }

    &.channel {
      margin-left: 0;

      .chat_msg {
        width: 80%;
        border-radius: 15px;

        .chat_msg__body {
          width: 100%;
          max-width: 100%;
        }
      }
    }

    &_msg {
      display: inline-block;
      margin-bottom: 8px;
      position: relative;

      &__body {
        width: auto;
        max-width: 303px;
        text-align: left;
        padding: 16px 16px 8px;
        position: relative;
        display: inline-block;
        line-height: 25px;
        font-size: 16px;
        font-weight: 500;

        .message {
          word-break: break-word;
          white-space: pre-line;
        }

        .file {
          display: flex;
          cursor: pointer;
          align-items: center;

          &_cover {
            width: 59px;
            height: 78px;
          }

          &_format {
            position: absolute;
            font-size: 16px;
            line-height: normal;
            display: block;
            margin: auto;
            color: $sub-black;
          }

          &_icon {
            width: 59px;
            height: 78px;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 32px;
          }

          &_info {
            display: flex;
            flex-direction: column;
          }

          &_name {
            max-width: 180px;
            overflow: hidden;
            display: inline-block;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          &_description {
            font-size: 12px;
            opacity: .5;
            line-height: 1.2;

            span { margin-right: 4px; }
          }
        }
      }

      &.income {
        margin-left: 52px;
        background: $light-theme-line;
        border-radius: 0 15px 15px;
        color: $sub-black;
      }

      &.outcome {
        background: $blue;
        color: $white;
        border-radius: 15px 15px 0;

        .chat_msg__time {
          opacity: .5;
          color: $white;
        }
      }

      &.img {
        border-radius: 15px 15px 0;

        .chat_msg__body { padding: 0 0 25px; }

        .file_img {
          border-radius: 15px 15px 0 0;
          max-width: 100%;
        }

        &.group {
          .chat_msg__name {
            padding-left: 16px;
            margin-bottom: 10px;
          }

          .file_img { border-radius: 0; }
        }
      }

      &.group,
      &.company {
        margin-left: 50px;

        .chat_msg__body { padding-top: 15px; }
      }

      &__time {
        text-align: right;
        color: $grey-light2;
        line-height: normal;
        font-size: 12px;
        font-weight: 600;
      }

      &__name {
        line-height: normal;
        font-size: 12px;
        font-weight: 600;
        margin-bottom: 6px;
        color: $grey-text;
        display: flex;
        align-items: center;

        & > span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: default;
        }

        .admin { color: $orange-3; }

        .moderator { color: $blue; }
      }

      &__action {
        display: block;
        width: 100%;
        border: 2px solid $blue;
        box-sizing: border-box;
        border-radius: 0 0 15px 15px;
        padding: 19px;
        background: $white;
        line-height: normal;
        font-size: 16px;
        font-weight: 600;
        text-align: center;
        color: $blue;
        text-decoration: none;
        position: relative;
        outline: none;

        &::before {
          content: '';
          position: absolute;
          top: -4px;
          left: -2px;
          background: $white;
          width: calc(100% + 4px);
          height: 2px;
        }
      }

      &__avatar > div {
        width: 35px;
        min-width: 35px;
        height: 35px;
        background-size: cover;
        position: absolute;
        border-radius: 50%;
        left: -50px;
      }

      &__company {
        margin-left: 6px;
        display: flex;
        align-items: center;

        span { color: $sub-black; }

        .company__avatar {
          display: inline-block;
          border-radius: 50%;
          width: 16px;
          min-height: 16px;
          height: 16px;
          background-size: cover;
          margin: -4px 6px 0;
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    .chat { margin-left: 0; }
  }

  @media screen and (max-width: 380px) {
    .chat_msg__body { max-width: 245px !important; }
  }
</style>
